import { useEffect, useRef, useState } from "react";
import { searchRetailers, createRetailer, assignRetailerRoute } from "../../api/retailersApi";
import Modal from "./Model";
import { routesFetcher } from "../../services/onlandsite";
import { UserCircle } from "lucide-react";

export default function RetailerAutocomplete({ value, onChange }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [addRetailerPrompt, setAddRetailerPrompt] = useState(false);
  const [retailerName, setRetailerName] = useState("");
  const [selectedRouteId, setSelectedRouteId] = useState();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [routes, setRoutes] = useState([]);

  const loadRoutes = async () => {
    setRoutes(await routesFetcher());
  }
  useEffect(() => {
    loadRoutes()
  }, [])

  const debounceRef = useRef(null);

  const handleSearch = (q) => {
    setSearch(q);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!q) {
      setOptions([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        const data = await searchRetailers(q);
        setOptions(data?.data || []);
      } finally {
        setLoading(false);
      }
    }, 500);
  };

  const [updateRouteOnly, setUpdateRouteOnly] = useState(false);
  const [updateRetailer, setUpdateRetailer] = useState();

  const handleAssignRetailerRoute = async () => {
    assignRetailerRoute({ routeId: selectedRouteId, retailerId: updateRetailer?.id });
    setUpdateRouteOnly(false)
    handleSelect(updateRetailer, true)
  }

  const handleSelect = (retailer, updated) => {
    if (retailer.route_name === "OTHER" && !updated) {
      setUpdateRetailer(retailer);
      setUpdateRouteOnly(true);
    } else {
      onChange(retailer);
      setOptions([retailer])
      setSearch(retailer.name);
    }
  };

  const handleCreateRetailer = async () => {
    if (retailerName) {
      const { data } = await createRetailer({ name: retailerName, route_id: selectedRouteId, mobile_number: phoneNumber });
      setRetailerName("");
      setPhoneNumber("");
      setSelectedRouteId();
      setAddRetailerPrompt(false);
      onChange(data);
    } else {
      alert("Retailer Name required.")
    }
  };

  return (
    <div className="space-y-3">
      {/* Input */}
      <div className="relative">
        <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
          {/* search icon */}
          <svg
            className="h-4 w-4 text-gray-400"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M12.9 14.32a8 8 0 111.414-1.414l3.39 3.39a1 1 0 01-1.415 1.415l-3.39-3.39zM14 8a6 6 0 11-12 0 6 6 0 0112 0z"
              clipRule="evenodd"
            />
          </svg>
        </span>
        <input
          className="w-full rounded-xl border border-gray-300 bg-white/80 pl-10 pr-10 py-2.5 text-sm text-gray-900 shadow-sm outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
          placeholder="Search retailer by name, phone, or city…"
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
        />
        {/* right-side loading indicator spot (non-blocking) */}
        {loading && (
          <span className="absolute inset-y-0 right-3 inline-flex items-center">
            <svg
              className="h-4 w-4 animate-spin text-gray-400"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="3"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z"
              />
            </svg>
          </span>
        )}
      </div>
      {/* Modal to update route for already existing retailer */}
      <Modal isOpen={updateRouteOnly} noButtons={true} title={"Update route"} onClose={() => setUpdateRouteOnly(false)}>
        <div className="text-md font-semibold mb-2">Retailer Name - {updateRetailer?.name}</div>
        {/* Route Select Dropdown */}
        {routes?.length &&
          < select
            name="routeId"
            value={selectedRouteId}
            onChange={(e) => setSelectedRouteId(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white pl-2 pr-3 py-2 text-sm text-gray-900 shadow-sm outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
          >
            <option value="">Select a route</option>
            {routes?.length && routes?.map((route) => (
              <option key={route.id} value={route.id}>
                {route.route_name} ({route.route_code})
              </option>
            ))}
          </select>
        }
        <div>

          <button
            type="button"
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-green-700 focus:outline-none focus:ring-4 focus:ring-indigo-200 active:scale-95"
            onClick={() =>
              handleAssignRetailerRoute(updateRetailer, true)
            }
          >
            Update
          </button>
        </div>
      </Modal>

      {/* Results */}
      {options?.length ? (
        <div className="rounded-2xl border border-gray-200 bg-white/80 shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2">
            <p className="text-xs font-medium text-gray-600">
              {options.length} {options.length === 1 ? "result" : "results"}
            </p>
          </div>
          <ul
            className="max-h-56 overflow-y-auto divide-y divide-gray-100"
            role="listbox"
            aria-label="Retailer options"
          >
            {options.map((opt) => {
              const isSelected = value?.id === opt.id;
              const initials =
                (opt?.name || "")
                  .split(" ")
                  .map((s) => s[0])
                  .slice(0, 2)
                  .join("")
                  .toUpperCase() || "R";

              return (
                <li
                  key={opt.id}
                  className={`cursor-pointer px-4 py-3 transition ${isSelected
                    ? "bg-indigo-50"
                    : "hover:bg-gray-50 focus:bg-gray-50"
                    }`}
                  onClick={() => handleSelect(opt)}
                  role="option"
                  aria-selected={isSelected}
                >
                  <div className="flex items-center gap-3">
                    {/* Avatar */}
                    <div
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${isSelected
                        ? "bg-indigo-600 text-white"
                        : "bg-gray-200 text-gray-700"
                        }`}
                    >
                      {initials}
                    </div>

                    {/* Main content */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span
                          className={`truncate text-sm capitalize ${isSelected
                            ? "font-semibold text-indigo-900"
                            : "font-medium text-gray-900"
                            }`}
                          title={opt?.name}
                        >
                          {opt?.name} - {opt?.route_name} - {opt?.mobile_number || ""}
                        </span>
                        {isSelected && (
                          <span className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold text-indigo-700">
                            Selected
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                        {opt?.mobile_number && (
                          <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-0.5">
                            <svg
                              className="mr-1 h-3 w-3"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path d="M2 3a1 1 0 011-1h3.28a1 1 0 01.948.684l.894 2.683a1 1 0 01-.502 1.196l-1.357.68a11.042 11.042 0 005.516 5.516l.68-1.357a1 1 0 011.196-.502l2.683.894A1 1 0 0118 13.72V17a1 1 0 01-1 1h-1C8.82 18 2 11.18 2 3V3z" />
                            </svg>
                            {opt.mobile_number}
                          </span>
                        )}
                        {opt?.city && (
                          <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-0.5">
                            <svg
                              className="mr-1 h-3 w-3"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 2a6 6 0 00-6 6c0 4.418 6 10 6 10s6-5.582 6-10a6 6 0 00-6-6zm0 8a2 2 0 110-4 2 2 0 010 4z"
                                clipRule="evenodd"
                              />
                            </svg>
                            {opt.city}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Chevron */}
                    <svg
                      className={`h-4 w-4 shrink-0 ${isSelected ? "text-indigo-600" : "text-gray-400"
                        }`}
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {/* Empty state / Add dummy */}
      {options.length === 0 && !loading && (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-white/60 px-6 py-8 text-center">
          <div className="mb-3 rounded-full bg-gray-100 p-3">
            <svg
              className="h-6 w-6 text-gray-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M8 9a3 3 0 116 0 3 3 0 01-6 0z" />
              <path
                fillRule="evenodd"
                d="M2 10a8 8 0 1115.546 2.916l.921 2.764a1 1 0 01-1.265 1.265l-2.764-.921A8 8 0 012 10zm8-6a6 6 0 100 12A6 6 0 0010 4z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <p className="text-sm font-medium text-gray-900">
            No retailers found
          </p>
          <p className="mt-1 max-w-sm text-xs text-gray-500">
            Try a different search. Or quickly add a placeholder retailer to
            proceed.
          </p>
          <button
            type="button"
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus:ring-4 focus:ring-indigo-200 active:scale-95"
            onClick={() =>
              setAddRetailerPrompt(true)
            }
          >
            <span className="text-base leading-none">＋</span> Add
            Retailer
          </button>

          <Modal icon={<div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-pink-100 sm:mx-0 sm:size-10">
            <UserCircle aria-hidden="true" className="size-6 text-pink-600" />
          </div>} isOpen={addRetailerPrompt} onSubmit={() => handleCreateRetailer()} onClose={() => setAddRetailerPrompt(false)} title="Enter new retailer name" >

            <input className="w-full rounded-lg border border-gray-300 bg-white pl-2 pr-3 py-2 text-sm text-gray-900 shadow-sm outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
              type="text" name="retailerName" onChange={(e) => setRetailerName(e.target.value)} value={retailerName} placeholder="Srinu Kiranam - NTPC" />

            {/* Route Select Dropdown */}
            {routes?.length &&
              < select
                name="routeId"
                value={selectedRouteId}
                onChange={(e) => setSelectedRouteId(e.target.value)}
                className="mt-4 w-full rounded-lg border border-gray-300 bg-white pl-2 pr-3 py-2 text-sm text-gray-900 shadow-sm outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
              >
                <option value="">Select a route</option>
                {routes?.length && routes?.map((route) => (
                  <option key={route.id} value={route.id}>
                    {route.route_name} ({route.route_code})
                  </option>
                ))}
              </select>
            }
            <div className="text-sm font-sm mt-4">Phone Number</div>
            <input className="w-full rounded-lg border border-gray-300 bg-white pl-2 pr-3 py-2 text-sm text-gray-900 shadow-sm outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
              type="text" name="phoneNumber" onChange={(e) => setPhoneNumber(e.target.value)} value={phoneNumber} placeholder="98555xxxxx" />

          </Modal>
        </div>
      )
      }
    </div >
  );
}
